import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ChevronRight } from 'lucide-react';
import type { Domain } from '@/lib/types';

/**
 * Domain card — the dominant Dashboard tile. Reads like a binder tab:
 * a small mono "stamp" of the domain slug above a Newsreader title.
 * Hover lifts subtly; the trailing chevron nudges right to signal
 * forward motion without being precious.
 */
export function DomainCard({ domain }: { domain: Domain }) {
  return (
    <Link
      to={`/domains/${domain.slug}`}
      className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
      data-testid={`domain-card-${domain.slug}`}
    >
      <Card
        className={
          'h-full transition-all duration-200 ' +
          'border-rule group-hover:border-primary/30 group-hover:shadow-md group-hover:-translate-y-0.5 ' +
          'group-hover:bg-card'
        }
      >
        <CardHeader className="space-y-1.5 pb-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {domain.slug}
          </p>
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="font-display text-lg leading-tight">{domain.name}</CardTitle>
            <ChevronRight
              className="h-4 w-4 shrink-0 mt-1 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
              aria-hidden
            />
          </div>
        </CardHeader>
        {domain.description ? (
          <CardContent className="pt-0 pb-5">
            <CardDescription className="line-clamp-2">{domain.description}</CardDescription>
          </CardContent>
        ) : null}
      </Card>
    </Link>
  );
}
